// Admin · Roles view — read-only matrix of what each role can do.
// Phase C: reads /admin/roles. Role labels + blurbs + the
// capability-group layout are presentation metadata, hardcoded here (the API
// returns just role/capabilities/seat_count).
//
// Field renames vs. MOCK.ROLES:
//   MOCK.id           → role         ('admin' | 'planner' | 'reporter')
//   MOCK.label        → ROLE_LABELS[role]
//   MOCK.blurb        → ROLE_BLURBS[role]
//   MOCK.seats        → seat_count
//   MOCK.capabilities → capabilities (RoleCapEntry[]) — indexed by `capability`

import { Fragment } from 'react';
import { Link } from '@tanstack/react-router';
import { Icons } from '@/components/primitives';
import { useAdminRoles } from '@/api/hooks';
import { RoleBadge } from './admin-users';
import { PageActions } from '@/components/page-actions';
import type { Role, RoleCapEntry, RoleEntry } from '@/api/types';

type CapLevel = RoleCapEntry['level'];

interface CapabilityGroup {
  label: string;
  caps: readonly (readonly [capability: string, label: string])[];
}

// Presentation metadata for the three fixed roles. The API only ships
// machine-readable seat counts + capability levels; copy lives here so it
// can change without a backend deploy.
const ROLE_LABELS: Record<Role, string> = { admin: 'Admin', planner: 'Planner', reporter: 'Reporter' };
const ROLE_BLURBS: Record<Role, string> = {
  admin: 'Org leads and operators. Full read/write on everything, plus user, role, and integration management.',
  planner: 'Day-to-day campaign work. Reads and edits campaigns/audiences/creatives, can be scoped to specific productions.',
  reporter: 'Stakeholders who need numbers but not controls. Read-only across reporting and backend.',
};
const ROLE_HINTS: Record<Role, string> = {
  admin: 'Recommended for: org leads, ops engineers',
  planner: 'Recommended for: media buyers, campaign managers',
  reporter: 'Recommended for: producers, agency stakeholders, finance',
};

// Capability matrix layout — what cap shows up under which group, in what
// order. Stable presentation order keeps the page legible row over row.
const CAPABILITY_GROUPS: readonly CapabilityGroup[] = [
  { label: 'Campaigns', caps: [
    ['campaigns_view',   'View campaigns'],
    ['campaigns_edit',   'Edit campaigns'],
    ['campaigns_launch', 'Launch campaigns'],
  ]},
  { label: 'Audiences', caps: [
    ['audiences_view', 'View audiences'],
    ['audiences_edit', 'Edit audiences'],
  ]},
  { label: 'Creatives', caps: [
    ['creatives_view', 'View creatives'],
    ['creatives_edit', 'Edit creatives'],
  ]},
  { label: 'Reporting', caps: [
    ['reports_view',   'View reports'],
    ['reports_export', 'Export reports'],
  ]},
  { label: 'Backend & integrations', caps: [
    ['backend_view',         'View backend (productions / events / venues)'],
    ['backend_edit',         'Edit backend records'],
    ['pixels_view',          'View pixel setup'],
    ['pixels_manage',        'Manage pixels'],
    ['pixel_samples_view',   'View captured pixel payloads (may contain PII)'],
    ['integrations_manage',  'Manage integrations (Meta / TTD)'],
  ]},
  { label: 'Admin', caps: [
    ['users_manage',  'Manage users'],
    ['roles_manage',  'View / change roles'],
    ['logs_view',     'View logs'],
    ['audit_view',    'View audit trail'],
    ['config_manage', 'Manage batch configuration'],
    ['billing_manage', 'Manage invoicing (monthly Xero billing)'],
    ['tokens_issue',  'Create API keys and connect apps'],
    ['tokens_revoke', 'Revoke API keys and app connections'],
  ]},
];

export interface RolesViewProps {
  setView?: (viewId: string) => void;
}

function RolesView({ setView: _setView }: RolesViewProps) {
  const { data: roles = [], isLoading } = useAdminRoles();
  const seatTotal = roles.reduce((sum, r) => sum + r.seat_count, 0);

  return (
    <div className="tbl-frame">
      <div className="page-hd">
        <div>
          <h1 className="page-title">Roles</h1>
        </div>
        <PageActions>
          {/* A link, not a button: it goes somewhere, so it can be opened in a
              new tab and announced as one. It had no handler at all before. */}
          <Link className="btn" data-phone="inline" to="/admin/users" preload="intent"><span>{Icons.user}</span><span className="phone-long">Manage users</span><span className="phone-short">Users</span></Link>
        </PageActions>
      </div>

      <div role="region" aria-label="Role permissions" tabIndex={0} style={{padding:'22px 22px 60px', overflow:'auto'}}>

        {/* Top intro */}
        <div style={{maxWidth:780, marginBottom:24}}>
          <div className="mono" style={{fontSize:10, letterSpacing:'.14em', color:'var(--ink-3)', textTransform:'uppercase', marginBottom:6}}>
            ROLE-BASED ACCESS CONTROL
          </div>
          <div style={{fontSize:14, color:'var(--ink-1)', lineHeight:1.55}}>
            Every user is one of three roles. Roles are fixed in this version — you can’t edit
            their permissions, but admins can change which role a user has from the
            <span style={{color:'var(--ink)', fontWeight:600}}> Users</span> page.
          </div>
        </div>

        {isLoading && roles.length === 0 && (
          <div className="muted" style={{padding:'40px 0', textAlign:'center'}}>Loading roles…</div>
        )}

        {/* Role cards. A card's share is of the same users its count holds:
            `seat_count` leaves disabled users out (users::seat_counts_by_role),
            so dividing by the whole list made three single-seat roles read
            25% each beside one disabled user. */}
        {roles.length > 0 && (
          <div className="stack-phone" style={{display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:16, marginBottom:32}}>
            {roles.map(r => (
              <RoleCard key={r.role} role={r} totalUsers={seatTotal}/>
            ))}
          </div>
        )}

        {/* Capability matrix */}
        {roles.length > 0 && (
          <>
            <div className="mono" style={{fontSize:10, letterSpacing:'.14em', color:'var(--ink-3)', textTransform:'uppercase', marginBottom:10}}>
              CAPABILITY MATRIX
            </div>
            <div className="panel" style={{padding:0, overflow:'hidden'}}>
              <div className="capgrid capgrid-header">
                <div></div>
                {roles.map(r => (
                  <div key={r.role} className="capgrid-col-head">
                    <RoleBadge role={r.role}/>
                  </div>
                ))}
              </div>

              {CAPABILITY_GROUPS.map(g => (
                <Fragment key={g.label}>
                  <div className="capgrid-group-head">{g.label}</div>
                  {g.caps.map(([cap, label]) => (
                    <div key={cap} className="capgrid capgrid-row">
                      <div className="capgrid-label">{label}</div>
                      {roles.map(r => (
                        <div key={r.role} className="capgrid-cell">
                          <span className="capgrid-role-label">{ROLE_LABELS[r.role]}</span>
                          <CapCell level={lookupCap(r.capabilities, cap)}/>
                        </div>
                      ))}
                    </div>
                  ))}
                </Fragment>
              ))}
            </div>

            {/* Legend */}
            <div style={{display:'flex', gap:24, marginTop:18, flexWrap:'wrap', fontSize:11.5, color:'var(--ink-2)'}}>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <CapCell level="full"/><span>Full access</span>
              </div>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <CapCell level="view"/><span>Read-only</span>
              </div>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <CapCell level="own"/><span>Own records only</span>
              </div>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <CapCell level="none"/><span>No access</span>
              </div>
            </div>

            <div className="muted" style={{marginTop:24, fontSize:11.5, fontStyle:'italic', maxWidth:780}}>
              Need finer control — like read-write on Productions but read-only on Audiences for a specific user? Custom roles are on the roadmap. In the meantime, talk to your admin about Production scope (Planner role can be scoped to specific productions).
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function lookupCap(capabilities: readonly RoleCapEntry[], cap: string): CapLevel {
  // capabilities is RoleCapEntry[]; find matching capability name.
  const entry = capabilities.find(c => c.capability === cap);
  return entry ? entry.level : 'none';
}

// ── RoleCard ───────────────────────────────────────────────────────────
interface RoleCardProps {
  role: RoleEntry;
  totalUsers: number;
}

function RoleCard({ role, totalUsers }: RoleCardProps) {
  const pct = totalUsers > 0 ? Math.round((role.seat_count / totalUsers) * 100) : 0;
  return (
    <div className="panel" style={{padding:18, position:'relative', overflow:'hidden'}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10}}>
        <RoleBadge role={role.role}/>
        <div style={{textAlign:'right'}}>
          <div style={{fontFamily:'var(--font-display)', fontWeight:800, fontSize:24, lineHeight:1}}>{role.seat_count}</div>
          <div className="mono" style={{fontSize:9.5, letterSpacing:'.1em', color:'var(--ink-3)', textTransform:'uppercase', marginTop:2}}>
            {role.seat_count === 1 ? 'user' : 'users'} · {pct}%
          </div>
        </div>
      </div>
      <h3 style={{fontFamily:'var(--font-display)', fontWeight:800, fontSize:24, letterSpacing:'.01em', textTransform:'uppercase', margin:'4px 0 8px'}}>
        {ROLE_LABELS[role.role]}
      </h3>
      <div style={{fontSize:12.5, color:'var(--ink-1)', lineHeight:1.5, marginBottom:8}}>
        {ROLE_BLURBS[role.role]}
      </div>
      <div style={{marginTop:10, paddingTop:10, borderTop:'1px solid var(--line-2)'}}>
        <div className="mono" style={{fontSize:10, color:'var(--ink-3)', letterSpacing:'.04em'}}>
          {ROLE_HINTS[role.role]}
        </div>
      </div>
    </div>
  );
}

// ── CapCell — renders the cell in the matrix per access level ─────────
interface CapCellProps {
  level: CapLevel;
}

function CapCell({ level }: CapCellProps) {
  if (level === 'full') {
    return <span className="cap-pill cap-full" title="Full access">●</span>;
  }
  if (level === 'view') {
    return <span className="cap-pill cap-view" title="Read-only">◐</span>;
  }
  if (level === 'own') {
    return <span className="cap-pill cap-own" title="Own records only"><span className="cap-own-dot">●</span></span>;
  }
  return <span className="cap-pill cap-none" title="No access">○</span>;
}

export { RolesView };
